import { useMemo, useState } from 'react';
import type { ClientRow } from './api';
import { ago, caption, count, money, needsYou, phone, plural, tradeTown } from './format';
import { Strip, StripKey } from './Strip';

type Filter = 'all' | 'needs' | 'setup' | 'live' | 'off';

const isLive = (c: ClientRow) => c.active && c.setup.live === 'done';
const isSettingUp = (c: ClientRow) => c.active && c.setup.live !== 'done';
const TEST: Record<Filter, (c: ClientRow) => boolean> = {
  all: () => true,
  needs: needsYou,
  setup: isSettingUp,
  live: isLive,
  off: (c) => !c.active,
};

/** Every shop at a glance: setup, what needs you, leads, texts, costs, last sign-in. */
export function Clients({ clients, error, onRetry, onAdd }: {
  clients: ClientRow[] | null;
  error: string;
  onRetry: () => void;
  onAdd: () => void;
}) {
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  // The demo shop goes last: it's LeadsIRL's own.
  const all = useMemo(() => [...(clients ?? [])].sort((a, b) => Number(a.demo) - Number(b.demo) || a.businessName.localeCompare(b.businessName)), [clients]);
  const shops = all.filter((c) => !c.demo);
  const n = (f: Filter) => all.filter(TEST[f]).length;
  const q = query.trim().toLowerCase();
  const digits = q.replace(/\D/g, '');
  const shown = all.filter(TEST[filter]).filter((c) => !q
    || [c.businessName, c.clientId, c.city, c.industry].some((v) => v?.toLowerCase().includes(q))
    || (digits.length >= 3 && (c.phoneNumber ?? '').includes(digits)));
  const cost = all.reduce((t, c) => t + c.costMonthMicros, 0);
  const live = shops.filter(isLive).length;
  const settingUp = shops.filter(isSettingUp).length;
  const off = shops.filter((c) => !c.active).length;
  const needs = n('needs');

  const open = (c: ClientRow) => { window.location.hash = `client/${encodeURIComponent(c.clientId)}`; };

  return (
    <>
      <div className="top">
        <h1>Clients</h1>
        <button className="primary" type="button" onClick={onAdd}>Add contractor</button>
      </div>
      {clients && (
        <p className="summary">
          {shops.length === 0 ? 'No contractors yet. Add your first one.' : (
            <>
              {plural(shops.length, 'contractor')}: <b>{live} live</b>, {settingUp} setting up{off ? `, ${off} switched off` : ''}.{' '}
              {needs ? <b>{needs === 1 ? '1 needs you.' : `${needs} need you.`}</b> : 'Nothing needs you.'}{' '}
              {money(cost)} in costs this month.
            </>
          )}
        </p>
      )}

      <div className="tools">
        <div className="filters" role="group" aria-label="Show">
          {([['all', 'All'], ['needs', 'Needs you'], ['setup', 'Setting up'], ['live', 'Live'], ['off', 'Switched off']] as [Filter, string][])
            .filter(([f]) => f !== 'off' || n('off') > 0 || filter === 'off')
            .map(([f, label]) => (
              <button key={f} type="button" className={filter === f ? 'on' : ''} aria-pressed={filter === f} onClick={() => setFilter(f)}>
                {label} <i>{n(f)}</i>
              </button>
            ))}
        </div>
        <input className="search" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a contractor, number or town" aria-label="Find a contractor" />
      </div>

      {error && (
        <div className="banner problem" role="alert">
          <span>Could not load the clients. {error}</span>
          <button type="button" className="ghost" onClick={onRetry}>Try again</button>
        </div>
      )}

      <div className="sheet">
        <table className="clients">
          <thead>
            <tr>
              <th>Contractor</th>
              <th>Setup</th>
              <th className="num">Leads, 30 days</th>
              <th className="num">Texts, 30 days</th>
              <th className="num">Costs you this month</th>
              <th>Last active</th>
            </tr>
          </thead>
          <tbody>
            {!clients && !error && (
              <tr className="empty"><td colSpan={6}>Loading…</td></tr>
            )}
            {clients && shown.length === 0 && (
              <tr className="empty"><td colSpan={6}>{q ? 'Nobody matches.' : filter === 'needs' ? 'Nothing needs you right now.' : 'None here.'}</td></tr>
            )}
            {shown.map((c) => {
              const [tone, text] = caption(c);
              return (
                <tr key={c.clientId} onClick={() => open(c)}>
                  <td className="who">
                    <a href={`#client/${encodeURIComponent(c.clientId)}`} onClick={(e) => e.stopPropagation()}>{c.businessName}</a>
                    <span>{c.demo ? `Demo shop${c.city ? `, ${c.city}` : ''}` : tradeTown(c) || phone(c.phoneNumber) || c.clientId}</span>
                  </td>
                  <td className="c-setup">
                    <Strip setup={c.setup} />
                    <span className={`state ${tone}`}>{text}</span>
                  </td>
                  <td className="num c-leads">{c.leads30 ? count(c.leads30) : <span className="sub">0</span>}<small> leads</small></td>
                  <td className="num c-texts">{c.texts30 ? count(c.texts30) : <span className="sub">0</span>}</td>
                  <td className="num c-cost">{money(c.costMonthMicros)}</td>
                  <td className="c-login sub">{ago(c.lastActiveAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <StripKey />
    </>
  );
}
